import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useMemo, useState, useEffect } from 'react';
import { Calendar } from 'react-native-big-calendar';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { PreferenceSlot, Section } from '@youshift/shared/types';
import {
  addMonths,
  dateToString,
  getCustomDateRange,
  getFirstDayOfWeek,
  localeNormalizer,
  subtractMonths,
} from '@youshift/shared/utils';
import {
  useMapPrefSlots,
  useCreateInitialPrefLabels,
  useCreatePrefSlots,
  CreatePrefSlotsParams,
} from '@youshift/shared/hooks/mutations';

import { useItrContext } from '../../../../layouts/IterationRootLayout/IterationRootLayout';
import {
  CalendarUserPreference,
  generateTimedPrefSlots,
  PrefSlotsConfigRenderer,
} from '../../../../utils/calendar';
import { backendToLocalDate } from '../../../../utils/helpers';
import i18n from '../../../../utils/i18n';
import PrefLabelControlCenter from './PrefLabelControlCenter';
import { PrefSlotsMapAction, generateDefaultPrefConfiguration } from './utils';
import { PrefSlotsCalendarToolbar } from './PrefSlotsCalendarToolbar';
import { WelcomeModal } from './WelcomeModal';
import { useChainContext } from '../../../../layouts/ChainLayout';

export default function PreferenceSlotsConfig() {
  const {
    iteration,
    preferenceSlots,
    sectionsWithSlots,
    prefLabels,
    sectionSlotsDict,
    userPreferences,
  } = useItrContext();
  const { slotLabels } = useChainContext();

  const { t } = useTranslation();
  const { idItr } = useParams();
  const queryClient = useQueryClient();
  const locale = localeNormalizer(i18n.language);

  const [hasChanges, setHasChanges] = useState(false);
  const [calendarMonthStart, setCalendarMonthStart] = useState(
    new Date(...backendToLocalDate(iteration.start_day)),
  );
  const [selectedPrefSlots, setSelectedPrefSlots] = useState<Set<number>>(
    new Set(),
  );
  const [selectedTemporaryPrefSlots, setSelectedTemporaryPrefSlots] = useState<
    Set<number>
  >(new Set());
  const [activeId, setActiveId] = useState<number | null>(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(
    Object.keys(preferenceSlots).length === 0,
  );
  const [createPreferenceSlotModalOpen, setCreatePreferenceSlotModalOpen] =
    useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  const initialPrefSlotToSectionSlotMap = useMemo(() => {
    const mapping: { [id_pref_slot: number]: number[] } = {};
    Object.values(preferenceSlots).forEach(slot => {
      mapping[slot.id_pref_slot] = slot.section_slots;
    });
    return mapping;
  }, [preferenceSlots]);

  const [prefSlotToSectionSlotMap, setPrefSlotToSectionSlotMap] = useState(
    initialPrefSlotToSectionSlotMap,
  );
  // update mapping when preferenceSlots change (for example when we delete them)
  useEffect(() => {
    const mapping: { [id_pref_slot: number]: number[] } = {};
    Object.values(preferenceSlots).forEach(slot => {
      mapping[slot.id_pref_slot] = slot.section_slots;
    });
    setPrefSlotToSectionSlotMap(mapping);
  }, [preferenceSlots]);

  const sectionSlotToPrefSlotMap = useMemo(() => {
    const mapping: { [id_section_slot: number]: number | null } = {};
    Object.keys(sectionSlotsDict).forEach(id => {
      mapping[Number(id)] = null;
    });
    Object.entries(prefSlotToSectionSlotMap).forEach(
      ([prefSlotId, sectionSlotIds]) => {
        sectionSlotIds.forEach(sectionSlotId => {
          mapping[sectionSlotId] = Number(prefSlotId);
        });
      },
    );
    return mapping;
  }, [prefSlotToSectionSlotMap, sectionSlotsDict]);

  // TEMPORARY PREF SLOTS FOR CREATING NEW PREF SLOTS AND VISUALIZING THEM
  const [temporaryPrefSlots, setTemporaryPrefSlots] = useState<
    Record<number, PreferenceSlot>
  >({});
  const [
    temporaryPrefSlotToSectionSlotMap,
    setTemporaryPrefSlotToSectionSlotMap,
  ] = useState<{
    [id_pref_slot: number]: number[];
  }>({});
  // use effect to update the temporary pref slot to section slot map when the temporary pref slots change
  useEffect(() => {
    const mapping: { [id_pref_slot: number]: number[] } = {};
    Object.values(temporaryPrefSlots).forEach(slot => {
      mapping[slot.id_pref_slot] = slot.section_slots;
    });
    setTemporaryPrefSlotToSectionSlotMap(mapping);
  }, [temporaryPrefSlots]);

  const sectionSlotToTemporaryPrefSlotMap = useMemo(() => {
    const mapping: { [id_section_slot: number]: number | null } = {};
    Object.keys(sectionSlotsDict).forEach(id => {
      mapping[Number(id)] = null;
    });
    Object.entries(temporaryPrefSlotToSectionSlotMap).forEach(
      ([prefSlotId, sectionSlotIds]) => {
        sectionSlotIds.forEach(sectionSlotId => {
          mapping[sectionSlotId] = prefSlotId as unknown as number;
        });
      },
    );
    return mapping;
  }, [sectionSlotsDict, temporaryPrefSlotToSectionSlotMap]);

  const sectionsDict = useMemo(
    () =>
      sectionsWithSlots.reduce<Record<number, Section>>((acc, { section }) => {
        acc[section.id_section] = section;
        return acc;
      }, {}),
    [sectionsWithSlots],
  );

  const calendarTimedPrefSlots = useMemo(
    () =>
      generateTimedPrefSlots({
        pref_slots: preferenceSlots,
        section_slots: sectionSlotsDict,
        pref_labels: prefLabels,
        pref_slots_map: prefSlotToSectionSlotMap,
      }),
    [prefLabels, sectionSlotsDict, preferenceSlots, prefSlotToSectionSlotMap],
  );

  const calendarTimedTemporaryPrefSlots = useMemo(
    () =>
      generateTimedPrefSlots({
        pref_slots: temporaryPrefSlots,
        section_slots: sectionSlotsDict,
        pref_labels: prefLabels,
        pref_slots_map: temporaryPrefSlotToSectionSlotMap,
      }),
    [
      prefLabels,
      sectionSlotsDict,
      temporaryPrefSlots,
      temporaryPrefSlotToSectionSlotMap,
    ],
  );

  const orphanSlots = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(sectionSlotsDict).filter(
          ([_, slot]) =>
            sectionSlotToPrefSlotMap[slot.id_section_slot] === null &&
            sectionSlotToTemporaryPrefSlotMap[slot.id_section_slot] === null,
        ),
      ),
    [
      sectionSlotsDict,
      sectionSlotToPrefSlotMap,
      sectionSlotToTemporaryPrefSlotMap,
    ],
  );

  const mapPrefSlotsMutation = useMapPrefSlots(queryClient, {
    onSuccess: () => {
      setHasChanges(false);
      queryClient.invalidateQueries({
        queryKey: ['assignmentTool', String(idItr)],
      });
      toast.success(t('manager.preferencesConfig.savePreferenceSlotsSuccess'));
    },
  });

  const createInitialPrefLabelsMutation = useCreateInitialPrefLabels(
    queryClient,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['assignmentTool', String(idItr)],
        });
        toast.success(
          t('manager.preferencesConfig.createInitialPrefLabelsSuccess'),
        );
        setShowWelcomeModal(false);
      },
    },
  );

  const createPrefSlotsMutation = useCreatePrefSlots(queryClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['assignmentTool', String(idItr)],
      });
      setCreatePreferenceSlotModalOpen(false);
      toast.success(t('manager.preferencesConfig.savePreferenceSlotsSuccess'));
      setTemporaryPrefSlots({});
      setSelectedTemporaryPrefSlots(new Set());
      setTemporaryPrefSlotToSectionSlotMap({});
    },
  });

  const goToNextMonth = () => setCalendarMonthStart(prev => addMonths(prev, 1));
  const goToPreviousMonth = () =>
    setCalendarMonthStart(prev => subtractMonths(prev, 1));

  const onPressCorner = (prefSlot: CalendarUserPreference) => {
    const isTemporary = Object.values(temporaryPrefSlots).some(
      slot => slot.id_pref_slot === prefSlot.id_pref_slot,
    );
    const updateMap = isTemporary
      ? setSelectedTemporaryPrefSlots
      : setSelectedPrefSlots;
    updateMap(prev => {
      const newSet = new Set(prev);
      if (newSet.has(prefSlot.id_pref_slot)) {
        newSet.delete(prefSlot.id_pref_slot);
      } else {
        newSet.add(prefSlot.id_pref_slot);
      }
      return newSet;
    });
  };

  const handlePrefSlotsMapUpdate = (
    action: PrefSlotsMapAction,
    isTemporary: boolean,
  ) => {
    setHasChanges(true);

    const updateMap = isTemporary
      ? setTemporaryPrefSlotToSectionSlotMap
      : setPrefSlotToSectionSlotMap;

    updateMap(currentMap => {
      const newMap = { ...currentMap };

      switch (action.type) {
        case 'ORPHAN_SECTION_SLOT': {
          Object.entries(newMap).forEach(([prefSlotId, sectionSlots]) => {
            // workaround to handle the fact that prefSlotId is a string when using uuids in temporary slots
            const prefSlotIdNumber = isTemporary
              ? (prefSlotId as unknown as number)
              : Number(prefSlotId);
            if (sectionSlots.includes(action.sectionSlotId)) {
              newMap[prefSlotIdNumber] = sectionSlots.filter(
                id => id !== action.sectionSlotId,
              );
              if (newMap[prefSlotIdNumber].length === 0) {
                delete newMap[prefSlotIdNumber];
              }
            }
          });
          return newMap;
        }

        case 'ASSIGN_TO_PREF_SLOT': {
          newMap[action.prefSlotId] = [
            ...(newMap[action.prefSlotId] || []),
            action.sectionSlotId,
          ];
          return newMap;
        }

        default:
          return currentMap;
      }
    });
  };

  const getChangedMappings = () => {
    const changedMappings: { [id_section_slot: number]: number | null } = {};

    // Check all section slots in the current mapping
    Object.entries(sectionSlotToPrefSlotMap).forEach(
      ([sectionSlotId, currentPrefSlotId]) => {
        const initialPrefSlotId = Object.entries(
          initialPrefSlotToSectionSlotMap,
        ).find(([_, sectionSlots]) =>
          sectionSlots.includes(Number(sectionSlotId)),
        )?.[0];

        // If the mapping has changed, include it in the result
        if (Number(initialPrefSlotId) !== currentPrefSlotId) {
          changedMappings[Number(sectionSlotId)] = currentPrefSlotId;
        }
      },
    );

    return changedMappings;
  };

  const handleSaveChanges = () => {
    if (Object.keys(getChangedMappings()).length > 0) {
      mapPrefSlotsMutation.mutate({
        id_itr: iteration.id_itr,
        section_slot_to_pref_slot: getChangedMappings(),
      });
    }

    if (Object.keys(temporaryPrefSlots).length > 0) {
      createPrefSlotsMutation.mutate<CreatePrefSlotsParams>({
        id_itr: iteration.id_itr,
        pref_slots: Object.values(temporaryPrefSlots)
          .filter(
            temporaryPrefSlot =>
              temporaryPrefSlotToSectionSlotMap[temporaryPrefSlot.id_pref_slot],
          )
          .map(slot => ({
            id_pref_label: slot.id_pref_label,
            section_slots: temporaryPrefSlotToSectionSlotMap[slot.id_pref_slot],
            display_date: slot.display_date,
          })),
      });
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as number);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      const isTemporary = Object.values(temporaryPrefSlots).some(
        slot => slot.id_pref_slot === over.id,
      );
      handlePrefSlotsMapUpdate(
        {
          type: 'ASSIGN_TO_PREF_SLOT',
          sectionSlotId: active.id as number,
          prefSlotId: over.id as number,
        },
        isTemporary,
      );
    }
  };

  const handlePrepopulate = () => {
    const defaultConfig = generateDefaultPrefConfiguration(
      sectionSlotsDict,
      slotLabels,
    );
    createInitialPrefLabelsMutation.mutate({
      id_itr: iteration.id_itr,
      pref_labels: defaultConfig,
    });
  };

  const handleManualConfig = () => {
    setShowWelcomeModal(false);
  };

  return (
    <>
      <WelcomeModal
        isOpen={showWelcomeModal}
        isLoading={createInitialPrefLabelsMutation.isPending}
        onClose={() => setShowWelcomeModal(false)}
        onPrepopulate={handlePrepopulate}
        onManualConfig={handleManualConfig}
      />
      <div className="flex flex-row justify-between items-center">
        <PrefLabelControlCenter />
        <button
          onClick={handleSaveChanges}
          disabled={!hasChanges || mapPrefSlotsMutation.isPending}
          className={`px-4 py-2 rounded-md text-white ${
            hasChanges
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          {mapPrefSlotsMutation.isPending
            ? t('generic.saving')
            : t('generic.saveChanges')}
        </button>
      </div>

      <div className="flex flex-row justify-between items-center mb-2">
        <div className="flex flex-row gap-1">
          <button
            onClick={goToPreviousMonth}
            className="rounded-md border border-gray-300 px-3 text-sm hover:bg-gray-50"
            aria-label="previous-week"
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>
          <button
            aria-label="next-week"
            onClick={goToNextMonth}
            className="rounded-md border border-gray-300 px-3 text-sm hover:bg-gray-50"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
          <p className="text-md font-semibold text-blue-600">
            {dateToString(calendarMonthStart, 'month-year-local')}
          </p>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex flex-row items-start">
          <div className="flex flex-col min-w-[75vw]">
            <Calendar
              locale={locale}
              events={[
                ...calendarTimedPrefSlots,
                ...calendarTimedTemporaryPrefSlots,
              ]}
              mode="month"
              height={600}
              maxVisibleEventCount={10}
              date={calendarMonthStart}
              swipeEnabled={false}
              weekStartsOn={getFirstDayOfWeek(locale) === 7 ? 0 : 1}
              renderEvent={(prefSlot, touchableOpacityProps) => (
                <PrefSlotsConfigRenderer
                  userPreferencesForPrefSlot={
                    userPreferences[prefSlot.id_pref_slot]
                  }
                  prefSlot={prefSlot}
                  touchableOpacityProps={touchableOpacityProps}
                  sectionSlots={sectionSlotsDict}
                  sections={sectionsDict}
                  locale={locale}
                  selectedPrefSlots={selectedPrefSlots}
                  selectedTemporaryPrefSlots={selectedTemporaryPrefSlots}
                  onPressCorner={onPressCorner}
                  handlePrefSlotsMapUpdate={handlePrefSlotsMapUpdate}
                  temporaryPrefSlots={temporaryPrefSlots}
                />
              )}
            />
          </div>
          <PrefSlotsCalendarToolbar
            setHasChanges={setHasChanges}
            orphanSlots={orphanSlots}
            locale={locale}
            temporaryPrefSlots={temporaryPrefSlots}
            selectedPrefSlots={selectedPrefSlots}
            selectedTemporaryPrefSlots={selectedTemporaryPrefSlots}
            setTemporaryPrefSlots={setTemporaryPrefSlots}
            onSelectAllPrefSlots={() => {
              setSelectedPrefSlots(
                new Set(
                  Object.values(preferenceSlots).map(slot => slot.id_pref_slot),
                ),
              );
              setSelectedTemporaryPrefSlots(
                new Set(
                  Object.values(temporaryPrefSlots).map(
                    slot => slot.id_pref_slot,
                  ),
                ),
              );
            }}
            onClearPrefSlots={() => {
              setSelectedPrefSlots(new Set());
              setSelectedTemporaryPrefSlots(new Set());
            }}
            activeId={activeId}
            createPreferenceSlotModalOpen={createPreferenceSlotModalOpen}
            setCreatePreferenceSlotModalOpen={setCreatePreferenceSlotModalOpen}
          />
        </div>
      </DndContext>
    </>
  );
}
